import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type UseTanstackPaginationParams<T> = {
	initialPage?: number;
	initialPageSize?: number;
	filters?: T;
};

export const useTanstackPagination = <T extends Record<string, unknown>>({
	initialPage = 1,
	initialPageSize = 50,
	filters,
}: UseTanstackPaginationParams<T> = {}) => {
	const [page, setPage] = useState<number>(initialPage);
	const [pageSize, setPageSize] = useState<number>(initialPageSize);
	const prevFiltersRef = useRef<string | null>(null);

	const onChangePage = useCallback((newPage: number) => {
		setPage(newPage);
	}, []);

	const onChangePageSize = useCallback((newSize: number) => {
		setPageSize(newSize);
		setPage(1);
	}, []);

	useEffect(() => {
		if (!filters) return;

		const serializedFilters = JSON.stringify(filters);

		if (prevFiltersRef.current && prevFiltersRef.current !== serializedFilters) {
			setPage(1);
			setPageSize(initialPageSize);
		}
		prevFiltersRef.current = serializedFilters;
	}, [filters]);

	const pagination = useMemo(
		() => ({
			page,
			size: pageSize,
		}),
		[page, pageSize],
	);

	return {
		pagination,
		pageSize,
		onChangePage,
		onChangePageSize,
	};
};
